/**
 * SimonCat Agent Platform - Layout Component
 * React Router version (Vite)
 *
 * 侧边栏导航 + 顶部栏 + 主内容区
 */

import React, { useState, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';

interface NavItem {
  name: string;
  emoji: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { name: '首页', emoji: '🏠', path: '/simon-cat' },
  { name: 'AI Agent', emoji: '🤖', path: '/simon-cat/agents' },
  { name: '执行中心', emoji: '⚡', path: '/simon-cat/execution' },
  { name: 'Agent 市场', emoji: '🏪', path: '/simon-cat/marketplace' },
  { name: '知识库', emoji: '📚', path: '/simon-cat/knowledge' },
  { name: '模型中心', emoji: '🧠', path: '/simon-cat/models' },
  { name: '工作流', emoji: '🔄', path: '/simon-cat/workflows' },
];

export default function AgentPlatformLayout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/simon-cat') {
        return location.pathname === '/simon-cat' || location.pathname === '/simon-cat/';
      }
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  return (
    <div className="sc-forest-shell" style={styles.shell}>
      <div style={styles.ambientBg} />

      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, ...(collapsed ? styles.sidebarCollapsed : {}) }}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>
            <span style={styles.logoEmoji}>🐱</span>
            {!collapsed && (
              <div>
                <span style={styles.logoTitle}>SimonCat</span>
                <span style={styles.logoSub}>Agent Platform</span>
              </div>
            )}
          </div>
          <button style={styles.collapseBtn} onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '→' : '←'}
          </button>
        </div>

        <nav style={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.navItemActive : {}),
                }}
              >
                <span style={styles.navEmoji}>{item.emoji}</span>
                {!collapsed && <span style={styles.navName}>{item.name}</span>}
                {active && <div style={styles.activeIndicator} />}
              </Link>
            );
          })}
        </nav>

        <div style={styles.sidebarFooter}>
          {!collapsed && (
            <Link to="/billing" style={styles.upgradeCard}>
              <span>💎</span>
              <span>升级会员</span>
            </Link>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="sc-content-glass" style={styles.mainArea}>
        <header style={styles.header}>
          <div style={styles.headerLeft} />
          <div style={styles.headerRight}>
            <button style={styles.headerIcon}>🔔</button>
            <button style={styles.headerIcon}>⚙️</button>
            <div style={styles.userAvatar}>U</div>
          </div>
        </header>

        <main style={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    zIndex: 0,
    position: 'relative',
    /* 背景交由 .sc-forest-shell 伪元素承载（登录页森林原图） */
    background: 'transparent',
    color: 'var(--text-primary, #e2e8f0)',
    fontFamily: 'var(--font-sans, system-ui)',
  },
  ambientBg: {
    position: 'fixed',
    inset: 0,
    background: 'radial-gradient(ellipse 50% 40% at 20% 10%, rgba(99,102,241,0.04) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  sidebar: {
    width: 220,
    minHeight: '100vh',
    /* 侧边栏：半透明黑 + 强模糊，鹿的头部区域从右缘透出余光 */
    background: 'rgba(0, 0, 0, 0.45)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRight: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    transition: 'width 0.25s ease',
    flexShrink: 0,
  },
  sidebarCollapsed: {
    width: 64,
  },
  sidebarHeader: {
    padding: '16px 12px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  logoEmoji: {
    fontSize: 24,
  },
  logoTitle: {
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
    background: 'linear-gradient(135deg, #a5b4fc, #c084fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  logoSub: {
    display: 'block',
    fontSize: 9,
    color: 'var(--text-muted, #64748b)',
  },
  collapseBtn: {
    width: 22,
    height: 22,
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.03)',
    color: 'var(--text-muted, #64748b)',
    fontSize: 10,
    cursor: 'pointer',
  },
  nav: {
    flex: 1,
    padding: '8px 6px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto' as const,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 10px',
    borderRadius: 10,
    textDecoration: 'none',
    color: 'var(--text-muted, #94a3b8)',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
  },
  navItemActive: {
    background: 'rgba(99,102,241,0.1)',
    color: '#a5b4fc',
  },
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: '60%',
    background: 'linear-gradient(180deg, #6366f1, #a855f7)',
    borderRadius: '0 3px 3px 0',
  },
  navEmoji: {
    fontSize: 18,
    width: 24,
    textAlign: 'center',
  },
  navName: {
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  sidebarFooter: {
    padding: '8px 12px',
    borderTop: '1px solid rgba(255,255,255,0.04)',
  },
  upgradeCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 10px',
    borderRadius: 8,
    background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(245,158,11,0.05))',
    border: '1px solid rgba(251,191,36,0.15)',
    textDecoration: 'none',
    color: '#fbbf24',
    fontSize: 11,
    fontWeight: 500,
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    position: 'relative',
    zIndex: 1,
  },
  header: {
    height: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    background: 'rgba(0, 0, 0, 0.45)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  headerLeft: {},
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.03)',
    color: '#94a3b8',
    fontSize: 12,
    cursor: 'pointer',
  },
  userAvatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: 'white',
  },
  content: {
    flex: 1,
    padding: 20,
    overflowY: 'auto' as const,
  },
};
